var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { PreviewConsentMode, PreviewMetadata } from "healthex-common/preview.dtos";
import { ScreenshotMetadataStatus } from "healthex-common/screenshot.dtos";
import { StudyTranslationLanguage } from "healthex-common/study.dtos";
import { computed, observable } from "mobx";
import { NOT_LOADED } from "../Infra/RemoteData";
import { i18nGetDateFnsLocale } from "../Infra/i18next";
import { format } from "date-fns";
export class ConsentPreviewViewModel {
    constructor(opts) {
        this.previewConsentDialogOpen = false;
        this.patientConsentUIManager = opts.container.patientConsentUIManager;
        this.studyPDFPreviewUIManager = opts.container.studyPDFPreviewUIManager;
        this.getStudy = opts.getStudy;
        this.saveInformedConsent = opts.saveInformedConsent;
        this.variant = opts.variant;
    }
    get study() {
        return this.getStudy();
    }
    get previewText() {
        return this.variant === "add-project"
            ? "Preview how this information will appear to patients when the project team invites them to get involved. Make sure the details are accurate and patient-friendly. Once you’re satisfied, you’re ready to proceed."
            : "Preview how this information will appear to patients when a researcher invites them to participate.";
    }
    get includeRequestTypes() {
        return this.studyPDFPreviewUIManager.includeRequestTypes;
    }
    get includeGuided() {
        return this.studyPDFPreviewUIManager.includeGuided;
    }
    get previewScreenshots() {
        return this.studyPDFPreviewUIManager.previewScreenshots;
    }
    get activeSince() {
        var _a;
        if (this.study === NOT_LOADED)
            return "";
        return ((_a = this.study) === null || _a === void 0 ? void 0 : _a.versionLastUpdated)
            ? format(this.study.versionLastUpdated, "LLLL d, yyyy", { locale: i18nGetDateFnsLocale() })
            : "";
    }
    get supportedLanguages() {
        return this.studyPDFPreviewUIManager.generatableLanguages;
    }
    async previewConsent(previewConsentFlow) {
        if (this.saveInformedConsent)
            await this.saveInformedConsent();
        if (this.study !== NOT_LOADED && this.study) {
            await this.patientConsentUIManager.startInteractivePreview(this.study.id, this.study.studyVersionId, new PreviewMetadata({
                consentMode: PreviewConsentMode.REMOTE,
                consentFlow: previewConsentFlow,
                language: StudyTranslationLanguage.en,
            }));
            this.previewConsentDialogOpen = true;
        }
    }
    hidePreviewConsent() {
        this.previewConsentDialogOpen = false;
    }
    get pdfText() {
        return this.variant === "add-project"
            ? "Choose the experiences you'd like to generate as PDFs. After generation, you can download them directly."
            : "Generate and download PDF versions of the patient experience for your records. Previously generated PDFs are available for download at any time.";
    }
    async generateScreenshots(metadatas) {
        await this.studyPDFPreviewUIManager.generateScreenshots(metadatas);
    }
    get allButtonData() {
        const previewScreenshots = this.previewScreenshots;
        const completed = previewScreenshots.filter((screenshot) => screenshot.status === ScreenshotMetadataStatus.COMPLETED).length;
        const generatable = previewScreenshots.filter((screenshot) => screenshot.status === "NOT_GENERATED" && !screenshot.disableGeneration).length;
        const error = previewScreenshots.filter((screenshot) => screenshot.status === ScreenshotMetadataStatus.ERROR).length;
        if (completed > 0) {
            return { type: "DOWNLOAD", count: completed };
        }
        else if (generatable > 0) {
            if (generatable === previewScreenshots.filter((screenshot) => !screenshot.disableGeneration).length) {
                return { type: "GENERATE_ALL", count: generatable };
            }
            else {
                return { type: "GENERATE", count: generatable };
            }
        }
        else if (error > 0) {
            return { type: "RETRY", count: error };
        }
        else {
            return { type: "GENERATING", count: previewScreenshots.length };
        }
    }
    get screenshotsNeedingUpdate() {
        return this.previewScreenshots.filter((screenshot) => screenshot.canUpdate);
    }
}
__decorate([
    observable
], ConsentPreviewViewModel.prototype, "previewConsentDialogOpen", void 0);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "study", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "previewText", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "includeRequestTypes", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "includeGuided", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "previewScreenshots", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "activeSince", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "supportedLanguages", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "pdfText", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "allButtonData", null);
__decorate([
    computed
], ConsentPreviewViewModel.prototype, "screenshotsNeedingUpdate", null);
